import { ComponentProps, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ROUTES } from 'config';
import {
  CopyEmployeeGroupModal,
  UpdateEmployeeGroupModal,
} from 'containers/employees-groups';
import { ActionsMenu } from 'melp-design/components';
import { useConfirm } from 'store/confirm';
import { EmployeeGroup, useDeleteEmployeeGroup } from 'store/employee-groups';
import { useModalContext } from 'store/modal';
import { routerHistory } from 'utils/router-history';
import { usePredefinedToasts } from 'utils/Toast';
import { useMe } from 'state/Administrators';

interface Props {
  employeeGroup: EmployeeGroup;
  type?: ComponentProps<typeof ActionsMenu>['type'];
}

export const EmployeeGroupActions: FC<Props> = ({ type, employeeGroup }) => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();
  const predefinedToasts = usePredefinedToasts();
  const { confirmation } = useConfirm();
  const { canEditGroups, hasEditPermission } = useMe();

  const { mutate: deleteEmployeeGroup, isLoading: isDeleting } =
    useDeleteEmployeeGroup();

  return (
    <ActionsMenu
      type={type}
      items={[
        {
          label: t('actions.rename_group'),
          disabled: !canEditGroups || !hasEditPermission(['groups']),
          onClick: () => {
            openModal({
              component: UpdateEmployeeGroupModal,
              props: {
                employeeGroupId: employeeGroup.id,
                initialValues: { name: employeeGroup.name },
              },
            });
          },
        },
        {
          label: t('actions.make_copy'),
          disabled: !canEditGroups || !hasEditPermission(['groups']),
          onClick: () => {
            openModal({
              component: CopyEmployeeGroupModal,
              props: {
                employeeGroupId: employeeGroup.id,
                initialValues: { name: employeeGroup.name },
              },
            });
          },
        },
        {
          label: t('actions.delete'),
          disabled:
            isDeleting || !canEditGroups || !hasEditPermission(['groups']),
          color: 'error',
          onClick: async () => {
            if (employeeGroup.employeeCount > 0) {
              toast.error(t('groups.group_cannot_be_deleted'));
            } else {
              const { confirmed } = await confirmation(
                t('common.areYouSureToDelete', {
                  title: employeeGroup.name,
                  interpolation: { escapeValue: false },
                }),
                undefined,
                'danger',
              );

              if (confirmed) {
                deleteEmployeeGroup(
                  { employeeGroupId: employeeGroup.id },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.deleted();
                      routerHistory.push(ROUTES.employeeGroups.list);
                    },
                  },
                );
              }
            }
          },
        },
      ]}
    />
  );
};
